import { useEffect, useState } from 'react'
import { fetchExchangeSettings, updateExchangeSettings } from '@/api/exchange'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

function servicesToText(services) {
  return (services ?? []).join('\n')
}

function textToServices(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
}

export function ExchangeSettingsCard({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [savedServices, setSavedServices] = useState([])
  const [servicesText, setServicesText] = useState('')

  useEffect(() => {
    fetchExchangeSettings()
      .then((data) => {
        setSavedServices(data.excluded_services ?? [])
        setServicesText(servicesToText(data.excluded_services))
      })
      .catch(() => setError('Could not load Exchange settings.'))
      .finally(() => setLoading(false))
  }, [])

  const draftServices = textToServices(servicesText)
  const hasUnsavedChanges =
    servicesToText(draftServices) !== servicesToText(savedServices)

  async function handleSave(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const updated = await updateExchangeSettings({
        excluded_services: draftServices,
      })
      setSavedServices(updated.excluded_services ?? [])
      setServicesText(servicesToText(updated.excluded_services))
      setMessage('Exchange settings saved.')
    } catch {
      setError('Could not save Exchange settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Skeleton className="h-40 w-full" />
  }

  return (
    <div className="space-y-4">
      {!embedded ? (
        <Card>
          <CardHeader>
            <CardTitle>Exchange health</CardTitle>
            <CardDescription>
              Services that should not fail the Exchange health check when stopped.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <form onSubmit={handleSave} className="space-y-4">
        {embedded ? (
          <p className="text-sm text-muted-foreground">
            Automatic Exchange services that are expected to stop after start. The Exchange
            health tab ignores these when they are not running. Defaults include
            MSExchangeNotificationsBroker.
          </p>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="exchange-excluded-services">Excluded services</Label>
          <textarea
            id="exchange-excluded-services"
            rows={6}
            value={servicesText}
            onChange={(event) => setServicesText(event.target.value)}
            placeholder={'MSExchangeNotificationsBroker'}
            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          <p className="text-xs text-muted-foreground">
            One Windows service name per line. Matching is case-insensitive. Applies immediately
            to Exchange health snapshots already in the portal; also keep the same names in the
            exchange-health-status RMM script for future collections.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-emerald-600">{message}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving || !hasUnsavedChanges}>
            {saving ? 'Saving…' : 'Save Exchange settings'}
          </Button>
          <p className="text-xs text-muted-foreground">
            {savedServices.length} service{savedServices.length === 1 ? '' : 's'} excluded
          </p>
        </div>
      </form>
    </div>
  )
}
